/**
 * Browse All Products flyout — dynamic featured rail.
 * Per Claude Design handoff §5 + Jon directive 2026-05-26 12:56 AEST
 * (click-only drill, not hover).
 *
 * Listens for custom events broadcast by VerticalTabsMenu when an L1 or
 * L2 is clicked, shows the matching <aside class="mm-rail" data-l1-key>
 * + its matching <div class="mm-rail__body" data-l2-key> within, hides
 * the rest. Updates the rail heading label.
 *
 * Lazy cards (header slim-down 2026-10-14): the header renders only the
 * rail SHELL (head/foot + one skeleton placeholder body per rail). The
 * real bodies (~150 product cards) come from the section
 * sections/megamenu-rails.liquid, fetched ONCE per menu via the Section
 * Rendering API on the first pointerenter/focusin/click/touchstart of the
 * nav, or on idle after window load — whichever is first. Placeholders
 * are swapped for the fetched bodies so the final DOM matches the old
 * inline render. If the fetch fails the rails stay hidden; the nav itself
 * never depends on this script.
 *
 * Selector contract (must match header-vertical-tabs-menu.liquid +
 * megamenu-featured-rail.liquid):
 *   [data-vertical-tabs-panel][data-rails-menu] → flyout root + linklist handle
 *   .mm-rail[data-l1-key="<handle>"]      → rail for that L1
 *   .mm-rail__body[data-l2-key="<handle>"]→ per-L2 card set
 *   .mm-rail__body[data-default]          → fallback (L1) cards
 *   .mm-rail__body[data-rail-placeholder][data-rail-key] → skeleton
 *   [data-l2-label]                       → rail heading text node
 *   section response: [data-mm-rails][data-menu-handle] > [data-rail-src]
 *
 * Events:
 *   vtm:l1-active { l1Key, l1Index }
 *   vtm:l2-active { l2Key, l2Index, parentL1Key }
 */

(() => {
  // Idempotent: the header (and this <script>) may be re-inserted by the
  // Theme Editor; listeners below are document-level and must bind once.
  if (window.__mmFeaturedRailInit) return;
  window.__mmFeaturedRailInit = true;

  const SECTION_ID = "megamenu-rails";
  const PANEL_SELECTOR = "[data-vertical-tabs-panel]";
  const railCache = new Map(); // menu handle → Promise<Map<l1Key, Element>>
  const panelState = new WeakMap(); // panel → { load, observer }
  const wiredPanels = new Set();
  let idleDone = false;

  function railsUrl(handle) {
    let root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    if (root.charAt(root.length - 1) !== "/") root += "/";
    const params = new URLSearchParams({ sort_by: handle, section_id: SECTION_ID });
    // Same-origin fetch keeps the preview cookie; also forward an explicit
    // preview_theme_id so unpublished/draft previews render this theme.
    const previewId = new URLSearchParams(window.location.search).get("preview_theme_id");
    if (previewId) params.set("preview_theme_id", previewId);
    return `${root}collections/all?${params.toString()}`;
  }

  function fetchRails(handle) {
    if (railCache.has(handle)) return railCache.get(handle);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);
    const promise = fetch(railsUrl(handle), {
      credentials: "same-origin",
      headers: { Accept: "text/html" },
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`megamenu-rails HTTP ${res.status}`);
        return res.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const root = doc.querySelector("[data-mm-rails]");
        if (!root || root.getAttribute("data-menu-handle") !== handle) {
          throw new Error("megamenu-rails: menu mismatch");
        }
        const byKey = new Map();
        root.querySelectorAll("[data-rail-src]").forEach((src) => {
          byKey.set(src.getAttribute("data-rail-src"), src);
        });
        if (byKey.size === 0) throw new Error("megamenu-rails: empty");
        return byKey;
      });
    // Drop failures from the cache so a later section reload can retry.
    // The timer covers the whole exchange (headers + body); aborting a
    // settled fetch is a no-op, so it is cleared once the chain settles —
    // a hung request reaches fail() (rails hidden) instead of parking
    // the skeleton forever.
    promise
      .catch(() => railCache.delete(handle))
      .finally(() => clearTimeout(timeoutId));
    railCache.set(handle, promise);
    return promise;
  }

  function init(panel) {
    if (!panel || panel.dataset.mmRailWired === "1") return;
    panel.dataset.mmRailWired = "1";

    const railsByL1 = new Map();
    panel.querySelectorAll(".mm-rail").forEach((rail) => {
      railsByL1.set(rail.dataset.l1Key, rail);
    });
    if (railsByL1.size === 0) return;

    let activeL1 = null;
    let activeL2 = null;
    let activeL2Label = null;

    function showRail(l1Key, l2Key, l2Label) {
      const failed = panel.dataset.mmRailFailed === "1";
      railsByL1.forEach((rail, key) => {
        const matches = !failed && key === l1Key;
        rail.hidden = !matches;
        if (!matches) return;

        // Swap body. A pending skeleton stays visible for any L2.
        rail.querySelectorAll(".mm-rail__body").forEach((body) => {
          const isMatch = body.hasAttribute("data-rail-placeholder")
            ? true
            : l2Key
              ? body.dataset.l2Key === l2Key
              : body.hasAttribute("data-default");
          body.hidden = !isMatch;
        });

        // Update heading label.
        const labelEl = rail.querySelector("[data-l2-label]");
        if (labelEl) {
          labelEl.textContent = (
            l2Label ||
            rail.dataset.l1Label ||
            ""
          ).trim();
        }
      });
    }

    // Initialise: show the rail matching the L1 the Liquid marked
    // active on first render. Falls back to the first rail in the DOM.
    function pickInitialL1() {
      const activeL1Row = panel.querySelector("[data-l1-row].active");
      if (activeL1Row && railsByL1.has(activeL1Row.dataset.l1Key)) {
        return activeL1Row.dataset.l1Key;
      }
      return railsByL1.keys().next().value;
    }

    function inject(byKey) {
      if (!panel.isConnected) return;
      panel.querySelectorAll("[data-rail-placeholder]").forEach((ph) => {
        const src = byKey.get(ph.dataset.railKey);
        if (!src) {
          // Header and section disagree on this L1 (e.g. cached at different
          // times) → drop the whole rail rather than show an empty column.
          const rail = ph.closest(".mm-rail");
          if (rail) {
            rail.hidden = true;
            railsByL1.delete(rail.dataset.l1Key);
          }
          ph.remove();
          return;
        }
        const frag = document.createDocumentFragment();
        Array.from(src.children).forEach((child) => {
          frag.appendChild(document.importNode(child, true));
        });
        ph.replaceWith(frag);
      });
      panel.dataset.mmRailLoaded = "1";
      if (activeL1) showRail(activeL1, activeL2, activeL2Label);
    }

    function fail() {
      panel.dataset.mmRailFailed = "1";
      railsByL1.forEach((rail) => {
        rail.hidden = true;
      });
    }

    let loadStarted = false;
    function load() {
      if (loadStarted) return;
      loadStarted = true;
      // Old/inline markup (no placeholders) → nothing to fetch. Keeps this
      // asset safe to deploy ahead of the header markup change.
      if (!panel.querySelector("[data-rail-placeholder]")) return;
      const handle = panel.dataset.railsMenu;
      if (!handle) {
        fail();
        return;
      }
      fetchRails(handle).then(inject, fail);
    }

    activeL1 = pickInitialL1();
    if (activeL1) showRail(activeL1, null, null);

    // L1 click handler from VerticalTabsMenu.
    panel.addEventListener("vtm:l1-active", (e) => {
      activeL1 = e.detail.l1Key;
      activeL2 = null;
      activeL2Label = null;
      showRail(activeL1, null, null);
    });

    // L2 click handler from VerticalTabsMenu.
    panel.addEventListener("vtm:l2-active", (e) => {
      const { l2Key, parentL1Key } = e.detail;
      const l2Row = panel.querySelector(
        `[data-l2-row][data-l2-key="${CSS.escape(l2Key)}"]`,
      );
      const l2Label =
        l2Row?.querySelector(".vtm-link-label")?.textContent?.trim() || "";
      activeL1 = parentL1Key || activeL1;
      activeL2 = l2Key;
      activeL2Label = l2Label;
      showRail(activeL1, l2Key, l2Label);
    });

    // Lazy-load triggers: first intent on the nav. Capture phase so the
    // VerticalTabsMenu trigger's stopPropagation() can't swallow them;
    // passive + no preventDefault → nav behaviour is untouched.
    const intentRoot =
      panel.closest(".nav-main") || panel.closest("vertical-tabs-menu") || panel;
    const intentOpts = { capture: true, passive: true };
    ["pointerenter", "focusin", "click", "touchstart"].forEach((type) => {
      intentRoot.addEventListener(type, load, intentOpts);
    });

    // Reset when flyout closes.
    let observer = null;
    const verticalTabs = panel.closest("vertical-tabs-menu");
    if (verticalTabs) {
      observer = new MutationObserver(() => {
        if (!verticalTabs.classList.contains("is-open")) {
          activeL1 = pickInitialL1();
          activeL2 = null;
          activeL2Label = null;
          if (activeL1) showRail(activeL1, null, null);
        }
      });
      observer.observe(verticalTabs, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    panelState.set(panel, { load, observer });
    wiredPanels.add(panel);
    if (idleDone) scheduleIdle(panel);
  }

  function initRoot(root) {
    if (!root) return;
    if (root.matches && root.matches(PANEL_SELECTOR)) {
      init(root);
      return;
    }
    if (root.querySelectorAll) root.querySelectorAll(PANEL_SELECTOR).forEach(init);
  }

  // Idle prefetch only when the flyout trigger is actually rendered (the
  // vertical-tabs menu is hidden on mobile) — intent events still load it.
  function loadPanel(panel) {
    const state = panelState.get(panel);
    if (!state || !panel.isConnected) return;
    const menu = panel.closest("vertical-tabs-menu") || panel;
    if (menu.getClientRects().length === 0) return;
    state.load();
  }

  function scheduleIdle(panel) {
    const run = () => {
      if (panel) {
        loadPanel(panel);
      } else {
        idleDone = true;
        wiredPanels.forEach(loadPanel);
      }
    };
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(run, { timeout: 4000 });
    } else {
      window.setTimeout(run, 1500);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initRoot(document));
  } else {
    initRoot(document);
  }

  if (document.readyState === "complete") {
    scheduleIdle();
  } else {
    window.addEventListener("load", () => scheduleIdle(), { once: true });
  }

  // Theme Editor: the header section is re-rendered on every setting
  // change. Wire the new panel (cached fetch → no refetch for the same
  // menu) and release the old one.
  document.addEventListener("shopify:section:load", (e) => initRoot(e.target));
  document.addEventListener("shopify:section:unload", (e) => {
    wiredPanels.forEach((panel) => {
      if (!e.target || !e.target.contains(panel)) return;
      const state = panelState.get(panel);
      if (state && state.observer) state.observer.disconnect();
      panelState.delete(panel);
      wiredPanels.delete(panel);
    });
  });
})();
